/* src/components/customGame/player/CustomGameCountdown.js */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { playCountdownSound } from '../../../utils/countdownSounds'; // Reusing existing sound utility
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCog } from '@fortawesome/free-solid-svg-icons'; // Using a generic icon

const CustomGameCountdown = ({ onComplete, onCancel, nBack, isChallenge /* other custom config info if needed */ }) => {
    // --- State ---
    const [countdown, setCountdown] = useState(3);
    const [pulsing, setPulsing] = useState(false);
    const [displayMessage, setDisplayMessage] = useState("Custom Game Starting!"); // Generic message

    // --- Refs ---
    const completedRef = useRef(false);
    const prevCountdownRef = useRef(null);

    // Log props for debugging
    useEffect(() => {
      console.log(`[CustomGameCountdown Render/Props Update] Received props: nBack=${nBack}, isChallenge=${isChallenge}`);
    }, [nBack, isChallenge]);

    // --- Effect: Play Sound and Trigger Pulse ---
    useEffect(() => {
        if (countdown > 0 && prevCountdownRef.current !== countdown) {
            playCountdownSound(countdown);
            prevCountdownRef.current = countdown;
            setPulsing(true);
            const pulseTimer = setTimeout(() => {
                if (completedRef.current === false) {
                    setPulsing(false);
                }
            }, 300);
            return () => clearTimeout(pulseTimer);
        }
    }, [countdown]);

    // --- Effect: Manage Countdown Timer ---
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(c => c - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (countdown === 0 && !completedRef.current) {
            completedRef.current = true;
            console.log("CustomGameCountdown: Countdown reached zero - calling onComplete.");
            onComplete();
        }
    }, [countdown, onComplete]);

    // --- Effect: Set Display Message (can be enhanced with config details) ---
    useEffect(() => {
        // For now, a generic message. Could be enhanced to show key custom parameters.
        let message = "Get Ready!";
        if (nBack) {
            message = `N-Back: ${nBack} Game Starting!`;
        }
        setDisplayMessage(message);
    }, [nBack]);


    // --- Rendering Logic ---
    let numberColor = "text-indigo-500";
    if (countdown === 1) numberColor = "text-amber-500";

    const levelLabel = isChallenge ? 'Challenge N-Level:' : 'N-Level:'; // Use nBack prop directly if simple N

    if (countdown <= 0) {
        return null; // Parent (CustomGamePlayerScreen) handles transition
    }

    return (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm z-20 animate-[fadeIn_0.3s_ease-in]">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4 border border-gray-200">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-3 text-gray-800">
                        {nBack ? `Custom N=${nBack} Game` : "Custom Game"}
                    </h2>
                    <div
                        className={`transition-transform duration-300 mb-6`}
                        style={{ transform: pulsing ? 'scale(1.15)' : 'scale(1)' }}
                    >
                        <span className={`text-8xl font-bold ${numberColor}`}>
                            {countdown}
                        </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-5 px-4 py-2 rounded-full bg-gray-50 inline-block border border-gray-200">
                        {/* Display a summary of the custom game if desired */}
                        {/* For now, just N-Level if available from props */}
                        {nBack !== undefined && (
                            <span>{levelLabel} <span className="font-semibold">{nBack}</span></span>
                        )}
                        {nBack === undefined && (
                             <span>Starting Custom Session</span>
                        )}
                    </div>
                </div>

                {/* Generic "Get Ready" or custom info message */}
                <div className="mt-5 mb-5">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-3 text-blue-700 text-left rounded-r-md shadow-sm">
                        <div className="flex items-start">
                            <FontAwesomeIcon icon={faCog} className="text-blue-500 mr-2 mt-0.5 flex-shrink-0 h-4 w-4" />
                            <div className="text-sm">{displayMessage}</div>
                        </div>
                    </div>
                </div>

                <div className="mt-5 pt-4 border-t border-gray-200 text-center">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors duration-150 flex items-center justify-center mx-auto border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                        title="Cancel game start"
                    >
                        <FontAwesomeIcon icon={faTimes} className="mr-1.5 h-3.5 w-3.5" />
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

CustomGameCountdown.propTypes = {
    onComplete: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    nBack: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Can be a number, or "Adaptive", "Manual" etc.
    isChallenge: PropTypes.bool, // Prop from standard game, may or may not be relevant here
    // Potentially add:
    // customConfigSummary: PropTypes.string, // A short summary of key custom settings
};

CustomGameCountdown.defaultProps = {
    isChallenge: false, // Custom games are generally not "challenges" in the old progression sense
};

export default CustomGameCountdown;